import { useEffect, useState } from "react";
import { useNeucleosWorldStore } from "./worldStore";

const pendingByReader = new WeakMap<
  (key: string) => Promise<number>,
  Map<string, Promise<number>>
>();

function sharedCountRead(
  readCount: (key: string) => Promise<number>,
  worldKey: string,
  grantRevision: number,
): Promise<number> {
  let pending = pendingByReader.get(readCount);
  if (!pending) {
    pending = new Map();
    pendingByReader.set(readCount, pending);
  }
  const key = JSON.stringify([worldKey, grantRevision]);
  const existing = pending.get(key);
  if (existing) return existing;
  const requests = pending;
  const shared = Promise.resolve()
    .then(() => readCount(worldKey))
    .finally(() => {
      if (requests.get(key) === shared) requests.delete(key);
    });
  requests.set(key, shared);
  return shared;
}

/** Lifecycle for a World badge: one pending read, visible polling and immediate address isolation. */
export function useWorldPollingCount(
  worldKey: string | null,
  readCount: (key: string) => Promise<number>,
  listeners: Set<() => void>,
  intervalMs: number,
): number {
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  const [answer, setAnswer] = useState<{
    worldKey: string;
    grantRevision: number;
    count: number;
  } | null>(null);
  useEffect(() => {
    if (worldKey === null) return;
    let current = true;
    let pending = false;
    let refreshAgain = false;
    const run = async () => {
      if (!current || document.visibilityState === "hidden") return;
      if (pending) {
        refreshAgain = true;
        return;
      }
      pending = true;
      try {
        const count = await sharedCountRead(readCount, worldKey, grantRevision);
        if (current) setAnswer({ worldKey, grantRevision, count });
      } catch {
        if (current) setAnswer({ worldKey, grantRevision, count: 0 });
      } finally {
        pending = false;
        if (refreshAgain) {
          refreshAgain = false;
          void run();
        }
      }
    };
    const refresh = () => {
      void run();
    };
    refresh();
    const timer = window.setInterval(refresh, intervalMs);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    listeners.add(refresh);
    return () => {
      current = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      listeners.delete(refresh);
    };
  }, [worldKey, readCount, listeners, intervalMs, grantRevision]);
  return worldKey !== null &&
    answer?.worldKey === worldKey &&
    answer.grantRevision === grantRevision
    ? answer.count
    : 0;
}
